/**
 * Notifications.jsx — Premium redesign
 *
 * DROP-IN replacement for the NotificationsPage component inside EmployeeDashboard.jsx.
 *
 * HOW TO USE:
 *   1. Copy this file next to EmployeeDashboard.jsx as  Notifications.jsx
 *   2. In EmployeeDashboard.jsx, add at the top:
 *        import NotificationsPage from './Notifications';
 *   3. Delete the old  const NotificationsPage = …  component (lines ~576-610).
 *   4. The existing call site stays untouched:
 *        <NotificationsPage
 *            notifications={notifications}
 *            unreadCount={unreadCount}
 *            onMarkAllRead={markAllNotificationsRead}
 *            onOpen={handleNotificationClick}
 *        />
 *
 * Props (same contract as before):
 *   notifications  – Array<{ id, type, title, message, time, read }>
 *   unreadCount    – number
 *   onMarkAllRead  – () => void
 *   onOpen         – (notification) => void
 */

import React, { useState, useMemo } from 'react';
import {
  Bell, Clock, Calendar, DollarSign, Target, FileText,
  Ticket, ShieldCheck, User, CheckCheck,
  Search, Sparkles, ChevronRight,
} from 'lucide-react';

/* ─── Theme (matches the rest of the HRMS) ─────────────────────────── */
const T = {
  green: '#1A3C2B', greenDeep: '#0E2118', greenMid: '#255238',
  greenTint: '#EDF3EF', greenGlow: 'rgba(26,60,43,0.1)',
  bg: '#F4F5F0', card: '#FFFFFF', line: '#E3E2DC', lineSoft: '#EEEEE9',
  ink: '#111418', body: '#3A4149', muted: '#687079', faint: '#9BA3AB',
  success: '#2F7D4F', successTint: '#E8F3EC',
  warning: '#B9822E', warningTint: '#FBF1E1',
  danger: '#C0413D', dangerTint: '#FCEAEA',
  info: '#3B6FA6', infoTint: '#EAF1FB',
};

/* ─── Notification type → icon + colour ────────────────────────────── */
const TYPE_META = {
  attendance:     { icon: Clock,        color: T.warning,  tint: T.warningTint,  label: 'Attendance'   },
  leave:          { icon: Calendar,     color: T.info,     tint: T.infoTint,     label: 'Leave'        },
  payroll:        { icon: DollarSign,   color: T.success,  tint: T.successTint,  label: 'Payroll'      },
  performance:    { icon: Target,       color: '#7E6BE0',  tint: '#EFEAFA',      label: 'Performance'  },
  document:       { icon: FileText,     color: T.info,     tint: T.infoTint,     label: 'Documents'    },
  ticket:         { icon: Ticket,       color: T.warning,  tint: T.warningTint,  label: 'Ticket'       },
  access_request: { icon: ShieldCheck,  color: T.danger,   tint: T.dangerTint,   label: 'Access'       },
  account:        { icon: User,         color: T.green,    tint: T.greenTint,    label: 'Account'      },
};
const fallbackMeta = { icon: Bell, color: T.green, tint: T.greenTint, label: 'General' };

const getMeta = (type) => TYPE_META[type] || fallbackMeta;

/* ─── Filter tabs ───────────────────────────────────────────────────── */
const TABS = ['All', 'Unread', 'Attendance', 'Leave', 'Payroll', 'Performance'];

/* ─── Helpers ───────────────────────────────────────────────────────── */
const relativeDate = (timeStr) => {
  if (!timeStr) return '';
  // timeStr is already formatted by mapNotification's timeAgo() call
  return timeStr;
};

/* ─── Sub-components ────────────────────────────────────────────────── */

const NotifRow = ({ notif, onOpen, isLast }) => {
  const [hovered, setHovered] = useState(false);
  const meta = getMeta(notif.type);
  const Icon = meta.icon;

  return (
    <li style={{ borderBottom: isLast ? 'none' : `1px solid ${T.lineSoft}` }}>
      <button
        onClick={() => onOpen(notif)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: '100%', display: 'flex', alignItems: 'flex-start', gap: 14,
          padding: '16px 20px', textAlign: 'left', border: 'none', cursor: 'pointer',
          background: hovered
            ? (notif.read ? '#F7F8F5' : T.greenTint + '80')
            : (notif.read ? T.card : T.greenTint + '40'),
          transition: 'background 0.15s',
          position: 'relative',
        }}
      >
        {/* Unread left accent bar */}
        {!notif.read && (
          <span style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: 3, background: T.green, borderRadius: '0 3px 3px 0',
          }} />
        )}

        {/* Icon bubble */}
        <span style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: notif.read ? T.lineSoft : meta.tint,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.15s',
          border: `1.5px solid ${notif.read ? T.line : meta.color + '30'}`,
        }}>
          <Icon size={17} color={notif.read ? T.faint : meta.color} strokeWidth={2} />
        </span>

        {/* Content */}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: 'block', fontSize: 13.5, fontWeight: notif.read ? 500 : 700,
            color: notif.read ? T.body : T.ink, marginBottom: 3,
          }}>
            {notif.title}
          </span>
          <span style={{
            display: 'block', fontSize: 12.5, color: T.muted,
            lineHeight: 1.5, marginBottom: 5,
          }}>
            {notif.message}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11, fontWeight: 600, color: T.faint,
            letterSpacing: '0.04em',
          }}>
            <span style={{
              width: 14, height: 14, borderRadius: 4,
              background: meta.tint, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={9} color={meta.color} />
            </span>
            {meta.label} · {relativeDate(notif.time)}
          </span>
        </span>

        {/* Unread dot + chevron */}
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, paddingTop: 2, flexShrink: 0 }}>
          {!notif.read && (
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: T.green,
              boxShadow: `0 0 0 3px ${T.greenTint}`,
            }} />
          )}
          <ChevronRight size={14} color={hovered ? T.green : T.faint}
            style={{ transition: 'color 0.15s, transform 0.15s', transform: hovered ? 'translateX(2px)' : 'none' }} />
        </span>
      </button>
    </li>
  );
};

const StatBadge = ({ count, label, color, tint }) => (
  <div style={{
    background: T.card, border: `1.5px solid ${T.line}`, borderRadius: 14,
    padding: '14px 18px', minWidth: 100, textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  }}>
    <p style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.02em' }}>{count}</p>
    <p style={{ fontSize: 11.5, color: T.muted, margin: '3px 0 0', fontWeight: 500 }}>{label}</p>
    <div style={{ width: 24, height: 3, borderRadius: 99, background: color, margin: '8px auto 0' }} />
  </div>
);

/* ─── Main export ───────────────────────────────────────────────────── */
export default function NotificationsPage({ notifications = [], unreadCount = 0, onMarkAllRead, onOpen }) {
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  const filtered = useMemo(() => {
    let list = notifications;
    if (activeTab === 'Unread') list = list.filter(n => !n.read);
    else if (activeTab !== 'All') {
      const key = activeTab.toLowerCase();
      list = list.filter(n => n.type === key || getMeta(n.type).label === activeTab);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(n =>
        n.title?.toLowerCase().includes(q) || n.message?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [notifications, activeTab, search]);

  const typeBreakdown = useMemo(() => {
    const counts = {};
    notifications.forEach(n => {
      const label = getMeta(n.type).label;
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 4);
  }, [notifications]);

  return (
    <div style={{ width: '100%' }}>
      <style>{`
        .notif-tab { transition: all 0.15s; cursor: pointer; border: none; }
        .notif-tab:hover { opacity: 0.85; }
        .mark-read-btn { transition: all 0.15s; cursor: pointer; }
        .mark-read-btn:hover { background: ${T.greenTint} !important; }
      `}</style>

      {/* ── Page header ──────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 20, marginBottom: 24, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 16,
            background: T.green, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 16px ${T.greenGlow}`, position: 'relative', flexShrink: 0,
          }}>
            <Bell size={24} color="#fff" strokeWidth={1.8} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: -5, right: -5,
                width: 20, height: 20, borderRadius: '50%',
                background: T.danger, border: '2px solid #fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800, color: '#fff',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: '-0.02em' }}>
              Notifications
            </h1>
            <p style={{ fontSize: 12.5, color: T.muted, margin: '3px 0 0' }}>
              {unreadCount > 0
                ? `${unreadCount} unread · ${notifications.length} total`
                : `All caught up · ${notifications.length} total`}
            </p>
          </div>
        </div>

        {/* Stat badges */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <StatBadge count={notifications.length} label="Total" color={T.green} />
          <StatBadge count={unreadCount} label="Unread" color={T.danger} />
          <StatBadge
            count={notifications.filter(n => n.read).length}
            label="Read" color={T.faint}
          />
        </div>
      </div>

      {/* ── Type breakdown row ────────────────────────────────── */}
      {typeBreakdown.length > 0 && (
        <div style={{
          display: 'flex', gap: 10, marginBottom: 22, flexWrap: 'wrap',
        }}>
          {typeBreakdown.map(([label, count]) => {
            const meta = Object.values(TYPE_META).find(m => m.label === label) || fallbackMeta;
            const Icon = meta.icon;
            return (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: T.card, border: `1.5px solid ${T.line}`,
                borderRadius: 12, padding: '9px 14px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: meta.tint, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={13} color={meta.color} strokeWidth={2} />
                </div>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.ink, margin: 0 }}>{count}</p>
                  <p style={{ fontSize: 10.5, color: T.muted, margin: 0 }}>{label}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Search + actions bar ─────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap',
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={14} color={searchFocused ? T.green : T.faint}
            style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'color 0.15s' }} />
          <input
            placeholder="Search notifications…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              width: '100%', height: 40, paddingLeft: 36, paddingRight: 14,
              borderRadius: 11, border: `1.5px solid ${searchFocused ? T.green : T.line}`,
              fontSize: 13.5, color: T.ink, outline: 'none',
              background: searchFocused ? T.card : T.bg,
              boxShadow: searchFocused ? `0 0 0 3px ${T.greenTint}` : 'none',
              transition: 'all 0.15s', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Mark all read */}
        {unreadCount > 0 && (
          <button
            className="mark-read-btn"
            onClick={onMarkAllRead}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, height: 40,
              padding: '0 16px', borderRadius: 11,
              border: `1.5px solid ${T.line}`, background: T.card,
              fontSize: 13, fontWeight: 600, color: T.green,
            }}
          >
            <CheckCheck size={15} strokeWidth={2} />
            Mark all read
          </button>
        )}
      </div>

      {/* ── Filter tabs ──────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap',
      }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab;
          const tabCount = tab === 'All' ? notifications.length
            : tab === 'Unread' ? unreadCount
            : notifications.filter(n => getMeta(n.type).label === tab || n.type === tab.toLowerCase()).length;
          return (
            <button
              key={tab}
              className="notif-tab"
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '6px 14px', borderRadius: 20,
                fontSize: 12.5, fontWeight: 600,
                background: isActive ? T.green : T.card,
                color: isActive ? '#fff' : T.body,
                border: `1.5px solid ${isActive ? 'transparent' : T.line}`,
                boxShadow: isActive ? '0 2px 8px rgba(26,60,43,0.15)' : 'none',
              }}
            >
              {tab}
              {tabCount > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 11, fontWeight: 700,
                  opacity: isActive ? 0.75 : 0.6,
                }}>
                  {tabCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Notification list card ───────────────────────────── */}
      <div style={{
        background: T.card, border: `1.5px solid ${T.line}`,
        borderRadius: 20, overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}>
        {/* Card header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '15px 20px', borderBottom: `1.5px solid ${T.lineSoft}`,
        }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: T.greenTint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bell size={15} color={T.green} strokeWidth={2} />
          </div>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>
            {activeTab === 'All' ? 'Inbox' : activeTab}
          </span>
          {filtered.length > 0 && (
            <span style={{
              marginLeft: 'auto', fontSize: 12, color: T.muted,
              background: T.lineSoft, borderRadius: 20, padding: '2px 10px', fontWeight: 600,
            }}>
              {filtered.length} {activeTab === 'Unread' ? 'unread' : 'notification' + (filtered.length !== 1 ? 's' : '')}
            </span>
          )}
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 20px' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 20,
              background: T.lineSoft, display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 16px',
            }}>
              {search ? (
                <Search size={28} color={T.faint} strokeWidth={1.5} />
              ) : activeTab === 'Unread' ? (
                <CheckCheck size={28} color={T.faint} strokeWidth={1.5} />
              ) : (
                <Bell size={28} color={T.faint} strokeWidth={1.5} />
              )}
            </div>
            <p style={{ fontSize: 15, fontWeight: 700, color: T.ink, margin: '0 0 6px' }}>
              {search ? 'No results found' : activeTab === 'Unread' ? 'All caught up!' : 'No notifications yet'}
            </p>
            <p style={{ fontSize: 13, color: T.muted, margin: 0 }}>
              {search
                ? `No notifications match "${search}"`
                : activeTab === 'Unread'
                  ? "You've read everything. Nice work."
                  : "New activity will appear here automatically."}
            </p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filtered.map((n, i) => (
              <NotifRow
                key={n.id}
                notif={n}
                onOpen={onOpen}
                isLast={i === filtered.length - 1}
              />
            ))}
          </ul>
        )}

        {/* Footer */}
        {filtered.length > 0 && (
          <div style={{
            padding: '12px 20px', borderTop: `1.5px solid ${T.lineSoft}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 12, color: T.faint }}>
              Showing {filtered.length} of {notifications.length}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color={T.faint} />
              <span style={{ fontSize: 11.5, color: T.faint }}>Auto-refreshes every 30s</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}